import {
  type FccDoorActuationError,
  FccTargetDoorStatus,
} from '@backoffice-frontend/graphql/subscription'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { Button, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDoorActuationErrorMessages } from '../locales/mappings'
import { useAdVehicleDoorStatus } from './useAdVehicleDoorStatus'

export const AdVehicleDoorStatus = () => {
  const { t } = useTranslation()
  const translateErrorMessage = useDoorActuationErrorMessages()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const {
    handleDoorRequest,
    requestedDoorStatus,
    currentDoorStatus,
    errors,
    isRequestPending,
  } = useAdVehicleDoorStatus()

  const isTelemetryUnavailable = currentDoorStatus === undefined
  // Track which errors have been shown to prevent duplicate toasts
  const shownErrorsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (isRequestPending) {
      shownErrorsRef.current.clear()
    }
  }, [isRequestPending])

  useEffect(() => {
    if (errors.length > 0) {
      const errorMessages = errors.map(error =>
        translateErrorMessage(error as FccDoorActuationError),
      )

      const contextMessage =
        requestedDoorStatus === FccTargetDoorStatus.Closed
          ? t('adFleetMap_vehicleVizDoorCouldNotBeClosed_caption')
          : t('adFleetMap_vehicleVizDoorCouldNotBeOpened_caption')

      errorMessages.forEach(errorMessage => {
        const fullMessage = `${[contextMessage, errorMessage].join('. ')}.`

        if (!shownErrorsRef.current.has(fullMessage)) {
          enqueueMoiaErrorNotification(fullMessage)
          shownErrorsRef.current.add(fullMessage)
        }
      })
    }
  }, [
    errors,
    requestedDoorStatus,
    t,
    translateErrorMessage,
    enqueueMoiaErrorNotification,
  ])

  return (
    <>
      <div
        css={{
          display: 'flex',
        }}
      >
        <Button
          label={
            isRequestPending
              ? requestedDoorStatus === FccTargetDoorStatus.Closed
                ? t('adFleetMap_closeDoor_button')
                : t('map_openDoor_button')
              : currentDoorStatus === FccTargetDoorStatus.Open
                ? t('adFleetMap_closeDoor_button')
                : t('map_openDoor_button')
          }
          variant="primary"
          size="compact"
          onClick={() => {
            handleDoorRequest()
          }}
          icon={isRequestPending ? <LoadingIndicator /> : undefined}
          disabled={isTelemetryUnavailable || isRequestPending}
        />
      </div>
      {isTelemetryUnavailable && (
        <Text size="md" color="reduced" css={{ maxWidth: '135px' }}>
          {t('adFleetMap_vehicleVizDoorInteractionNotAvailable_caption')}
        </Text>
      )}
    </>
  )
}
